import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { box } from '../shared/styles';
import Instruments from './Instruments';
import NonConformances from './NonConformances';
import Ledger from './Ledger';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';

/*
 * Quality (Task 6.6, ISO 9001): what needs attention, the equipment with its
 * upkeep and limits, and non-conformances with their corrective actions.
 * Every change asks why; the reason goes to the append-only ledger.
 */
function Dashboard({ t }) {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api.get('/iso/dashboard').then(({ data }) => setBoard(data)).catch((e) => setError(errorText(e)));
  }, []);
  if (error) return <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>;
  if (!board) return <p>{t('loading')}</p>;
  const tiles = [
    ['overdue', board.overdue, 'danger'], ['dueSoon', board.due_within_14_days, 'warning'],
    ['openExcursions', board.open_excursions, 'warning'], ['capaOverdue', board.capa_overdue, 'danger'],
    ['ncCritical', board.open_nonconformances.critical, 'danger'], ['ncMajor', board.open_nonconformances.major, 'warning'],
    ['ncMinor', board.open_nonconformances.minor, 'info'], ['outOfService', board.instruments.out_of_service, 'warning'],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }} data-testid="dashboard">
      {tiles.map(([key, value, tone]) => (
        <div key={key} style={{ ...box, marginBottom: 0, borderColor: value ? `var(--c-${tone}-border, var(--c-border))` : 'var(--c-border)' }}>
          <div style={{ fontSize: 28, fontWeight: 600, color: value ? `var(--c-${tone}-fg)` : 'var(--c-text-fg)' }}
            data-testid={`tile-${key}`}>{value}</div>
          <div style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t(`tile_${key}`)}</div>
        </div>
      ))}
    </div>
  );
}

export default function Quality() {
  const { t } = useTranslation('quality');
  const { user } = useAuth();
  const [tab, setTab] = useState('dashboard');
  // The ledger shows every change's content: auditors and system admins only (iso:export).
  const tabs = ['dashboard', 'instruments', 'nonconformances', ...(can(user, 'iso:export') ? ['ledger'] : [])];
  return (
    <div>
      <h2>{t('pageTitle')}</h2>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0 }}>{t('intro')}</p>
      <div role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {tabs.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} data-testid={`tab-${k}`}
            title={{ dashboard: t('tip.tabDashboard'), instruments: t('tip.tabInstruments'),
              nonconformances: t('tip.tabNonconformances'), ledger: t('tip.tabLedger') }[k]}
            onClick={() => setTab(k)} style={{ fontWeight: tab === k ? 600 : 400 }}>{t(`tab_${k}`)}</button>
        ))}
      </div>
      {tab === 'dashboard' && <Dashboard t={t} />}
      {tab === 'instruments' && <Instruments />}
      {tab === 'nonconformances' && <NonConformances />}
      {tab === 'ledger' && <Ledger />}
    </div>
  );
}
